import clsx from 'clsx';
import React, { useMemo } from 'react';
import { ArrowLeftIcon, ArrowRightIcon } from '../../../assets/new/icons';
import type { Pagination } from '../../../types/common';
import { cn } from '../../../utils/cn';
import { GreenTransparentButton } from '../../button/green-transparent-button';
import { TransparentButton } from '../../button/transparent-button';

type PaginationProps = {
  currentPage: number;
  setCurrentPage: (page: number) => void;

  meta: Pagination;

  className?: string;
};

export default function PaginationComponent({
  currentPage,
  setCurrentPage,
  className,
  meta,
}: PaginationProps) {
  const pageNumbers = useMemo(
    () => Array.from(Array(meta.pageCount).keys())?.map((i) => i + 1),
    [meta.pageCount]
  );
  const isPreviousDisabled = useMemo(() => currentPage === 1, [currentPage]);

  const isNextDisabled = useMemo(
    () => currentPage === meta.pageCount,
    [currentPage, meta.pageCount]
  );

  const shouldShowFirst = useMemo(() => currentPage > 2, [currentPage]);

  const shouldShowSpreadToLast = useMemo(
    () => currentPage < (meta.pageCount || 0) - 2,
    [currentPage, meta.pageCount]
  );

  return (
    <div className={cn('flex flex-row justify-center', className)}>
      <GreenTransparentButton
        className="mr-6 max-sm:mr-1"
        disabled={isPreviousDisabled}
        payload={'Previous'}
        IconBefore={<ArrowLeftIcon width="1.25rem" height="1.25rem" />}
        onClick={() => setCurrentPage(currentPage - 1)}
      />
      <div className="flex items-center gap-x-2 max-sm:gap-x-1">
        {shouldShowFirst && (
          <Button onClick={() => setCurrentPage(1)} payload={'1'} key={'1'} />
        )}
        {currentPage > 3 && <Ellipsis />}
        {currentPage - 1 > 0 && (
          <Button
            onClick={() => setCurrentPage(pageNumbers[currentPage - 2])}
            payload={String(currentPage - 1)}
            key={String(currentPage - 1)}
          />
        )}
        <Button
          onClick={() => setCurrentPage(pageNumbers[currentPage - 1])}
          payload={String(currentPage)}
          key={String(currentPage)}
          className="bg-green-100 [&>div>div]:text-green-900"
        />

        {currentPage + 1 < pageNumbers?.length && (
          <Button
            onClick={() => setCurrentPage(pageNumbers[currentPage])}
            payload={String(currentPage + 1)}
            key={String(currentPage + 1)}
          />
        )}
        {shouldShowSpreadToLast && <Ellipsis />}
        {currentPage !== pageNumbers?.length && (
          <Button
            onClick={() => setCurrentPage(pageNumbers[pageNumbers?.length - 1])}
            payload={String(pageNumbers?.length)}
            key={String(pageNumbers?.length)}
          />
        )}
      </div>
      <GreenTransparentButton
        className="ml-6 max-sm:ml-1"
        disabled={isNextDisabled}
        onClick={() => setCurrentPage(currentPage + 1)}
        payload={'Next'}
        IconAfter={<ArrowRightIcon width="1.25rem" height="1.25rem" />}
      />
    </div>
  );
}

const Button = ({
  payload,
  onClick,
  className,
  ...props
}: {
  payload: string;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'>) => (
  <TransparentButton
    {...props}
    className={clsx('size-9', className)}
    onClick={onClick}
    payload={payload}
  />
);

const Ellipsis = () => <span className="w-6 text-center max-sm:w-2">...</span>;
